// CSP-safe DOM builder. Everything is created via document.createElement and the
// CSSOM (element.style.*) — never innerHTML, never setAttribute('style'), never
// inline on* handlers. That's what lets the app run under a strict
// `style-src 'self'` (no 'unsafe-inline'): inline style *attributes* are blocked,
// but CSSOM writes are not.
import { effect } from './signal';

type StyleObj = Partial<CSSStyleDeclaration> & Record<string, string | number>;
export type Child = Node | string | number | null | undefined | false | Child[];

interface Props {
	class?: string;
	/** Applied via the CSSOM (el.style.setProperty), so it's CSP-safe. */
	style?: StyleObj;
	/** Reactive width %, applied via CSSOM — replaces inline style="width:..". */
	widthPct?: () => number;
	ref?: (el: HTMLElement) => void;
	[key: string]: unknown;
}

function applyStyle(el: HTMLElement, style: StyleObj): void {
	for (const [k, v] of Object.entries(style)) {
		// Custom properties (--x) go through setProperty; the rest via the typed
		// CSSStyleDeclaration. Both are CSSOM, both CSP-exempt.
		if (k.startsWith('--')) el.style.setProperty(k, String(v));
		else (el.style as unknown as Record<string, string>)[k] = String(v);
	}
}

export function h(tag: string, props: Props = {}, ...children: Child[]): HTMLElement {
	const el = document.createElement(tag);
	for (const [key, value] of Object.entries(props)) {
		if (value == null || value === false) continue;
		if (key === 'class') el.className = String(value);
		else if (key === 'style') applyStyle(el, value as StyleObj);
		else if (key === 'widthPct') {
			const getter = value as () => number;
			effect(() => el.style.setProperty('width', `${getter()}%`));
		} else if (key === 'ref') (value as (el: HTMLElement) => void)(el);
		else if (key.startsWith('on') && typeof value === 'function') {
			el.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
		} else el.setAttribute(key, String(value));
	}
	append(el, children);
	return el;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/** SVG element builder (createElementNS). Attributes only — SVG presentation
 * attributes (fill/stroke/d/...) are not governed by style-src. */
export function s(tag: string, attrs: Record<string, string | number> = {}, ...children: Child[]): SVGElement {
	const el = document.createElementNS(SVG_NS, tag);
	for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
	for (const c of children.flat()) {
		if (c == null || c === false) continue;
		el.append(c instanceof Node ? c : document.createTextNode(String(c)));
	}
	return el;
}

function append(parent: Node, children: Child[]): void {
	for (const child of children.flat(Infinity as 1)) {
		if (child == null || child === false) continue;
		parent.appendChild(child instanceof Node ? child : document.createTextNode(String(child)));
	}
}

/** Render renderFn into container, re-running (coarse replace) on signal change.
 * Fine for display views; live-edit forms use the finer-grained bind helpers.
 * Effects created inside renderFn are children of this one: each re-render
 * disposes the previous render's bindings. Returns the disposer (rarely needed
 * directly — route scopes and parent effects own cleanup). */
export function mount(container: Element, renderFn: () => Child): () => void {
	return effect(() => {
		const next = renderFn();
		container.replaceChildren(
			...(Array.isArray(next) ? next : [next])
				.flat(Infinity as 1)
				.filter((c): c is Node | string | number => c != null && c !== false)
				.map((c) => (c instanceof Node ? c : document.createTextNode(String(c))))
		);
	});
}
